import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./UserWatchlist.css";
import type { WatchlistDto } from "../api/watchlist.api";
import { useWatchlist } from "../context/WatchlistProvider";
import SearchHeader from "features/catalog/components/HomeLanding/HomeLandingHeader";
import ReleaseCard from "features/catalog/components/HomeLanding/ReleaseCard";
import { watchlistDtoToReleaseCardModel } from "../mappers/watchlistToReleaseCardModel";
import { EmptySearchState } from "features/catalog/components/EmptySearchState/EmptySearchState";
import { useRealCatalog } from "features/catalog/hooks/useRealCatalog";

const UserWatchlist: React.FC = () => {
  const { items, loading, removeByRowId } = useWatchlist();
  const { assets } = useRealCatalog();
  const [filteredItems, setFilteredItems] = useState<WatchlistDto[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const thumbnailByAssetUploadId = useMemo(() => {
    const map = new Map<number, string>();
    for (const asset of assets) {
      const id =
        typeof asset.assetUploadId === "number" && asset.assetUploadId > 0
          ? asset.assetUploadId
          : Number.parseInt(asset.id, 10);
      if (Number.isFinite(id) && id > 0 && asset.thumbnailUrl) {
        map.set(id, asset.thumbnailUrl);
      }
    }
    return map;
  }, [assets]);

  useEffect(() => {
    const filtered = items.filter((item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredItems(filtered);
  }, [searchTerm, items]);

  const clearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  const isSearchActive = searchTerm.trim().length > 0;
  const showEmptySearch = !loading && filteredItems.length === 0 && isSearchActive;
  const showEmptyWatchlist = !loading && filteredItems.length === 0 && !isSearchActive;

  return (
    <div className="user-watchlist">
      <SearchHeader
        alwaysVisible
        syncToSelector=".main-content"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      <div className="watchlist">
        {loading && (
          <div style={{ padding: "40px", textAlign: "center", color: "#aeaea0" }}>
            <p>Loading watchlist...</p>
          </div>
        )}

        {showEmptyWatchlist && (
          <div style={{ padding: "40px", textAlign: "center", color: "#aeaea0" }}>
            <p>Your watchlist is empty.</p>
          </div>
        )}

        {showEmptySearch && <EmptySearchState onClearSearch={clearSearch} />}

        {!loading && filteredItems.length > 0 && (
          <section className="homeLandingNewestReleasesSection">
            <div className="watchlist-release-cards">
              {filteredItems.map((item) => (
                <ReleaseCard
                  key={item.id}
                  item={watchlistDtoToReleaseCardModel(
                    item,
                    thumbnailByAssetUploadId.get(item.assetUploadId) ||
                      thumbnailByAssetUploadId.get(item.id)
                  )}
                  onWatchlistButtonClick={() => void removeByRowId(item.id)}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default UserWatchlist;